import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BACKPRESSURE_SUSTAIN_MS,
  isQueueMetricsUnavailable,
  QUEUE_DEPTH_WARN_THRESHOLD,
  type AdminQueueMetrics,
  type AdminQueueMetricsResponse,
} from '@/types/admin-queue-metrics.types';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/get-queue-metrics`;

const POLL_MS = 30_000;

type Status = 'idle' | 'loading' | 'success' | 'error';

interface UseAdminQueueMetricsResult {
  status: Status;
  data: AdminQueueMetrics | null;
  error: string | null;
  lastFetchedAt: Date | null;
  refetch: () => void;
  /** True once depth has been over threshold for BACKPRESSURE_SUSTAIN_MS. */
  backpressureAlertActive: boolean;
}

/**
 * Polls `get-queue-metrics` every 30s while the tab is visible (YAR-208).
 *
 * Deliberately a second, independent fetch rather than another field on
 * `useAdminMetrics`: pgmq can be unreachable while every product metric is
 * fine, and the queue section has to be able to fail on its own without
 * blanking the dashboard.
 */
export function useAdminQueueMetrics(
  password: string,
): UseAdminQueueMetricsResult {
  const [status, setStatus] = useState<Status>('idle');
  const [data, setData] = useState<AdminQueueMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [backpressureAlertActive, setBackpressureAlertActive] = useState(false);
  const inFlight = useRef(false);
  /**
   * When depth first went over the threshold, or null while it is under. A ref
   * and not state: it changes on every poll and must not re-render the page,
   * and it has to survive the render that flips the alert on.
   */
  const overThresholdSince = useRef<number | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!password || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) {
        const body = await res
          .json()
          .catch(() => ({ error: `HTTP ${res.status}` }));
        const base = body.error ?? `HTTP ${res.status}`;
        const msg =
          typeof body.message === 'string' && body.message !== body.error
            ? `: ${body.message}`
            : '';
        throw new Error(`${base}${msg}`);
      }

      const json: AdminQueueMetricsResponse = await res.json();

      // pgmq unreachable arrives as HTTP 200 by design. It is still an error
      // for this section — there is no depth to show — but it is a reported
      // one, so surface the server's message instead of a transport failure.
      if (isQueueMetricsUnavailable(json)) {
        setData(null);
        setStatus('error');
        setError(`pgmq_unavailable: ${json.message}`);
        setLastFetchedAt(new Date());
        return;
      }

      const depth = json.queues.email_processing.depth;
      const now = Date.now();
      if (depth > QUEUE_DEPTH_WARN_THRESHOLD) {
        overThresholdSince.current ??= now;
        setBackpressureAlertActive(
          now - overThresholdSince.current >= BACKPRESSURE_SUSTAIN_MS,
        );
      } else {
        // Dropping to or below the threshold clears both the timer and the
        // alert: the point of the latch is a *continuous* five minutes.
        overThresholdSince.current = null;
        setBackpressureAlertActive(false);
      }

      setData(json);
      setLastFetchedAt(new Date());
      setStatus('success');
      setError(null);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      inFlight.current = false;
    }
  }, [password]);

  useEffect(() => {
    fetchOnce();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchOnce();
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchOnce]);

  return {
    status,
    data,
    error,
    lastFetchedAt,
    refetch: fetchOnce,
    backpressureAlertActive,
  };
}
